import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
	MagnifyingGlassIcon,
	PlusIcon,
	FolderIcon,
} from '@heroicons/react/24/outline';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import {
	Badge,
	DataTable,
	FilterDropdown,
	StatusBadge,
} from '../../components/nextgen';
import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';

import { getProjects } from '../../api/projects';
import { getUserFullName } from '../../components/helpers/functions';

// Status color mapping
const getStatusColor = (status) => {
	const colors = {
		planning: 'blue',
		active: 'green',
		on_hold: 'yellow',
		completed: 'gray',
		archived: 'gray',
	};
	return colors[status] || 'gray';
};

// Status display names
const getStatusLabel = (status) => {
	const labels = {
		planning: 'Planning',
		active: 'Active',
		on_hold: 'On Hold',
		completed: 'Completed',
		archived: 'Archived',
	};
	return labels[status] || status;
};

// Progress bar component
const ProgressBar = ({ progress }) => (
	<div className="flex items-center gap-2">
		<div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
			<div
				className="h-full bg-blue-600 rounded-full transition-all duration-300"
				style={{ width: `${progress || 0}%` }}
			/>
		</div>
		<span className="text-sm text-gray-500">{progress || 0}%</span>
	</div>
);

// Stat Card
const StatCard = ({ label, value, valueColor = 'text-gray-900' }) => (
	<div className="bg-white border border-gray-200 rounded-lg p-5">
		<p className="text-sm text-gray-500 mb-1">{label}</p>
		<p className={`text-3xl font-semibold ${valueColor}`}>{value}</p>
	</div>
);

const Projects = () => {
	const navigate = useNavigate();
	const { id: projectIdFromUrl } = useParams();
	const { setOpen: openProjectOverview } = useModal('projectOverview');
	const { setOpen: openCreateProject } = useModal('createProject');

	// Filters
	const [search, setSearch] = useState('');
	const ACTIVE_STATUSES = ['planning', 'active', 'on_hold'];
	const [statusFilter, setStatusFilter] = useState(ACTIVE_STATUSES);
	const [categoryFilter, setCategoryFilter] = useState('all');

	// Data
	const {
		value: projectsData,
		execute: refreshProjects,
		status: loadingStatus,
	} = useAsync(getProjects, {
		defaultPayload: { pageSize: null },
	});

	useEffect(() => {
		refreshProjects({ pageSize: null });
	}, []);

	// Get unique categories for filter
	const categories = useMemo(() => {
		const cats = projectsData?.rows
			?.map((p) => p.category)
			.filter(Boolean)
			.reduce((acc, cat) => {
				if (!acc.find((c) => c.id === cat.id)) {
					acc.push(cat);
				}
				return acc;
			}, []);
		return cats || [];
	}, [projectsData]);

	// Process projects (search and filter)
	const processedProjects = useMemo(() => {
		let result = projectsData?.rows || [];

		// Search
		if (search) {
			const q = search.toLowerCase();
			result = result.filter(
				(p) =>
					p.projectCode?.toLowerCase().includes(q) ||
					p.name?.toLowerCase().includes(q),
			);
		}

		// Status filter
		if (statusFilter.length > 0) {
			result = result.filter((p) => statusFilter.includes(p.status));
		}

		// Category filter
		if (categoryFilter !== 'all') {
			result = result.filter((p) => p.categoryId === categoryFilter);
		}

		return result;
	}, [projectsData, search, statusFilter, categoryFilter]);

	const handleRowClick = (project) => {
		navigate(`/projects/${project.id}`);
		openProjectOverview({
			id: project.id,
			onClose: (path) => {
				navigate(path || '/projects');
				refreshProjects({ pageSize: null });
			},
		});
	};

	// Open project from URL if ID is present
	useEffect(() => {
		if (projectIdFromUrl) {
			openProjectOverview({
				id: projectIdFromUrl,
				onClose: () => {
					navigate('/projects');
					refreshProjects({ pageSize: null });
				},
			});
		}
	}, [projectIdFromUrl]);

	// Stats
	const total = projectsData?.rows?.length || 0;
	const planning =
		projectsData?.rows?.filter((p) => p.status === 'planning').length || 0;
	const active =
		projectsData?.rows?.filter((p) => p.status === 'active').length || 0;
	const onHold =
		projectsData?.rows?.filter((p) => p.status === 'on_hold').length || 0;
	const completed =
		projectsData?.rows?.filter((p) => p.status === 'completed').length || 0;

	const isLoading = loadingStatus === 'pending';

	// Table columns
	const columns = [
		{
			header: 'Project',
			accessor: 'name',
			cell: (row) => (
				<div className="flex items-center gap-3">
					<div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
						<FolderIcon className="w-4 h-4 text-blue-600" />
					</div>
					<div>
						<div className="font-medium text-gray-900">{row.name}</div>
						<div className="text-sm text-gray-500">{row.projectCode}</div>
					</div>
				</div>
			),
		},
		{
			header: 'Status',
			accessor: 'status',
			cell: (row) => (
				<StatusBadge color={getStatusColor(row.status)}>
					{getStatusLabel(row.status)}
				</StatusBadge>
			),
		},
		{
			header: 'Progress',
			accessor: 'progress',
			cell: (row) => <ProgressBar progress={row.progress} />,
		},
		{
			header: 'Manager',
			accessor: 'manager',
			cell: (row) =>
				row.manager ? getUserFullName(row.manager) : '-',
		},
		{
			header: 'Category',
			accessor: 'category',
			cell: (row) =>
				row.category ? (
					<Badge color="gray">{row.category.name}</Badge>
				) : (
					'-'
				),
		},
		{
			header: 'Milestones',
			accessor: 'milestones',
			cell: (row) => {
				const milestones = row.milestones || [];
				const completed = milestones.filter(
					(m) => m.status === 'completed',
				).length;
				return (
					<span className="text-sm text-gray-500">
						{completed}/{milestones.length}
					</span>
				);
			},
		},
	];

	return (
		<MainContent>
			<PageHeading
				title="Projects"
				actions={
					[{ onClick: () =>
							openCreateProject({
								onSuccess: () => refreshProjects({ pageSize: null }),
							}), name: 'New Project'}]
				}
			/>

			{/* Stats */}
			<div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
				<StatCard label="Total" value={total} />
				<StatCard label="Planning" value={planning} valueColor="text-blue-600" />
				<StatCard label="Active" value={active} valueColor="text-green-600" />
				<StatCard label="On Hold" value={onHold} valueColor="text-yellow-600" />
				<StatCard label="Completed" value={completed} valueColor="text-gray-600" />
			</div>

			{/* Filters */}
			<div className="flex flex-wrap gap-4 mb-6">
				{/* Search */}
				<div className="relative flex-1 min-w-[200px]">
					<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
					<input
						type="text"
						placeholder="Search projects..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
					/>
				</div>

				{/* Status Filter */}
				<FilterDropdown
					label="Status"
					value={statusFilter}
					onChange={setStatusFilter}
					multiple
					options={[
						{ value: 'planning', label: 'Planning' },
						{ value: 'active', label: 'Active' },
						{ value: 'on_hold', label: 'On Hold' },
						{ value: 'completed', label: 'Completed' },
						{ value: 'archived', label: 'Archived' },
					]}
				/>

				{/* Category Filter */}
				{categories.length > 0 && (
					<FilterDropdown
						label="Category"
						value={categoryFilter}
						onChange={setCategoryFilter}
						options={[
							{ value: 'all', label: 'All Categories' },
							...categories.map((cat) => ({
								value: cat.id,
								label: cat.name,
							})),
						]}
					/>
				)}
			</div>

			{/* Table */}
			<DataTable
				columns={columns}
				data={processedProjects}
				onRowClick={handleRowClick}
				isLoading={isLoading}
				emptyMessage="No projects found"
			/>
		</MainContent>
	);
};

export default Projects;
